// api-table — wireframe skin module
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: block;
    font-family: var(--run-wf-font-family, 'Courier New', monospace);
  }

  .at-root {
    display: flex;
    flex-direction: column;
    gap: var(--run-api-table-gap, 32px);
    outline: 1px dashed var(--run-wf-border, #aaa);
    padding: 12px;
  }

  .at-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .at-heading {
    margin: 0;
    font-size: 11px;
    font-weight: 400;
    font-family: var(--run-wf-font-family, 'Courier New', monospace);
    color: var(--run-wf-text, #222);
    letter-spacing: 0.1em;
    text-transform: uppercase;
    border-bottom: 1px dashed var(--run-wf-border, #aaa);
    padding-bottom: 4px;
  }

  .at-loading,
  .at-empty {
    font-size: 12px;
    font-family: var(--run-wf-font-family, 'Courier New', monospace);
    color: var(--run-wf-text-secondary, #666);
    padding: 16px 0;
  }

  .at-deprecated {
    opacity: 0.4;
  }

  .at-mono {
    font-family: var(--run-wf-font-family, 'Courier New', monospace);
    font-size: 0.9em;
    background: var(--run-wf-surface, #f0f0f0);
    padding: 1px 4px;
    border-radius: 2px;
  }

  .at-type-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
`;

function esc(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function typeBadges(typeStr) {
  if (!typeStr) return '—';
  const parts = String(typeStr).split('|').map(s => s.trim()).filter(Boolean);
  if (parts.length === 1) {
    return `<run-badge skin="wireframe" pill size="sm">${esc(parts[0])}</run-badge>`;
  }
  const chips = parts.map(p =>
    `<run-badge skin="wireframe" pill size="sm">${esc(p)}</run-badge>`
  ).join('');
  return `<span class="at-type-chips">${chips}</span>`;
}

function mono(val) {
  if (val == null || val === '') return '—';
  return `<code class="at-mono">${esc(val)}</code>`;
}

function slotName(name) {
  return name === '' || name == null ? '(default)' : esc(name);
}

function renderProperties(rows) {
  if (!rows || !rows.length) return '';
  const bodyRows = rows.map(p => `
    <run-table-row class="${p.deprecated ? 'at-deprecated' : ''}">
      <run-table-cell>${mono(p.name)}${p.deprecated ? ' [deprecated]' : ''}</run-table-cell>
      <run-table-cell>${p.attribute ? mono(p.attribute) : '—'}</run-table-cell>
      <run-table-cell>${typeBadges(p.type)}</run-table-cell>
      <run-table-cell>${p.default != null && p.default !== '' ? mono(p.default) : '—'}</run-table-cell>
      <run-table-cell>${esc(p.description)}</run-table-cell>
    </run-table-row>
  `).join('');

  return `
    <div part="section section-properties" class="at-section">
      <h3 part="section-heading" class="at-heading">Properties</h3>
      <run-table skin="wireframe" bordered size="s">
        <run-table-head>
          <run-table-column key="name"    width="160px">Property</run-table-column>
          <run-table-column key="attr"    width="140px">Attribute</run-table-column>
          <run-table-column key="type"    width="160px">Type</run-table-column>
          <run-table-column key="default" width="120px">Default</run-table-column>
          <run-table-column key="desc"    width="1fr">Description</run-table-column>
        </run-table-head>
        <run-table-body>${bodyRows}</run-table-body>
      </run-table>
    </div>
  `;
}

function renderSlots(rows) {
  if (!rows || !rows.length) return '';
  const bodyRows = rows.map(s => `
    <run-table-row>
      <run-table-cell>${mono(slotName(s.name))}</run-table-cell>
      <run-table-cell>${esc(s.description)}</run-table-cell>
    </run-table-row>
  `).join('');

  return `
    <div part="section section-slots" class="at-section">
      <h3 part="section-heading" class="at-heading">Slots</h3>
      <run-table skin="wireframe" bordered size="s">
        <run-table-head>
          <run-table-column key="name" width="200px">Slot</run-table-column>
          <run-table-column key="desc" width="1fr">Description</run-table-column>
        </run-table-head>
        <run-table-body>${bodyRows}</run-table-body>
      </run-table>
    </div>
  `;
}

function renderEvents(rows) {
  if (!rows || !rows.length) return '';
  const bodyRows = rows.map(e => `
    <run-table-row>
      <run-table-cell>${mono(e.name)}</run-table-cell>
      <run-table-cell>${e.type ? typeBadges(e.type) : '—'}</run-table-cell>
      <run-table-cell>${esc(e.description)}</run-table-cell>
    </run-table-row>
  `).join('');

  return `
    <div part="section section-events" class="at-section">
      <h3 part="section-heading" class="at-heading">Events</h3>
      <run-table skin="wireframe" bordered size="s">
        <run-table-head>
          <run-table-column key="name" width="200px">Event</run-table-column>
          <run-table-column key="type" width="200px">Detail Type</run-table-column>
          <run-table-column key="desc" width="1fr">Description</run-table-column>
        </run-table-head>
        <run-table-body>${bodyRows}</run-table-body>
      </run-table>
    </div>
  `;
}

function renderCssProps(rows) {
  if (!rows || !rows.length) return '';
  const bodyRows = rows.map(p => `
    <run-table-row>
      <run-table-cell>${mono(p.name)}</run-table-cell>
      <run-table-cell>${p.default != null && p.default !== '' ? mono(p.default) : '—'}</run-table-cell>
      <run-table-cell>${esc(p.description)}</run-table-cell>
    </run-table-row>
  `).join('');

  return `
    <div part="section section-css-props" class="at-section">
      <h3 part="section-heading" class="at-heading">CSS Custom Properties</h3>
      <run-table skin="wireframe" bordered size="s">
        <run-table-head>
          <run-table-column key="name"    width="260px">Property</run-table-column>
          <run-table-column key="default" width="200px">Default</run-table-column>
          <run-table-column key="desc"    width="1fr">Description</run-table-column>
        </run-table-head>
        <run-table-body>${bodyRows}</run-table-body>
      </run-table>
    </div>
  `;
}

function renderCssParts(rows) {
  if (!rows || !rows.length) return '';
  const bodyRows = rows.map(p => `
    <run-table-row>
      <run-table-cell>${mono(p.name)}</run-table-cell>
      <run-table-cell>${esc(p.description)}</run-table-cell>
    </run-table-row>
  `).join('');

  return `
    <div part="section section-css-parts" class="at-section">
      <h3 part="section-heading" class="at-heading">CSS Parts</h3>
      <run-table skin="wireframe" bordered size="s">
        <run-table-head>
          <run-table-column key="name" width="200px">Part</run-table-column>
          <run-table-column key="desc" width="1fr">Description</run-table-column>
        </run-table-head>
        <run-table-body>${bodyRows}</run-table-body>
      </run-table>
    </div>
  `;
}

const SECTION_RENDERERS = {
  properties:  (c) => renderProperties(c.properties),
  slots:       (c) => renderSlots(c.slots),
  events:      (c) => renderEvents(c.events),
  'css-props': (c) => renderCssProps(c.cssProps),
  'css-parts': (c) => renderCssParts(c.cssParts),
};

export function render(host) {
  if (host._loading) {
    host.shadowRoot.innerHTML = `
      <div part="base" class="at-root">
        <div part="loading" class="at-loading">Loading…</div>
      </div>
    `;
    return;
  }

  const contract = host.contract;

  if (!contract) {
    host.shadowRoot.innerHTML = `
      <div part="base" class="at-root">
        <div part="empty" class="at-empty">No contract data.</div>
      </div>
    `;
    return;
  }

  const sections = host._activeSections
    .map(key => SECTION_RENDERERS[key]?.(contract) || '')
    .filter(Boolean)
    .join('');

  host.shadowRoot.innerHTML = `
    <div part="base" class="at-root">
      ${sections || '<div part="empty" class="at-empty">No sections to display.</div>'}
    </div>
  `;

  host.shadowRoot.querySelectorAll('run-table').forEach(t => t._syncChildren?.());
}
